import { useCallback } from 'react'
import { useMemo } from 'react'

import { useDigitalProductContextOptional } from '@/shared/contexts/DigitalProductContext'
import { useRecord } from '@/shared/hooks'
import type { QuizProgress } from '@/types/quiz'
import type { UserProgress } from '@/types/userProgress'

const EMPTY_ARR: number[] = []
const EMPTY_OBJ: Record<string, unknown> = {}

export const useUserProgress = () => {
  // Optional porque a wallet da topbar monta durante o boot, antes do evento.
  // `digitalProductId` vem do host e já existe nesse momento; o que falta é o
  // dado do evento, e a query abaixo é desabilitada enquanto ele não chega.
  const digitalProductId =
    useDigitalProductContextOptional()?.digitalProductId ?? null

  const { record: userProgress, refetch: refetchProgress } = useRecord({
    r: `/user/digital_products/${digitalProductId}/gamification/progress`,
    // Sem evento não há o que buscar: `shouldFetch` desliga a query em vez de
    // deixar montar uma URL com `null` no meio.
    shouldFetch: Boolean(digitalProductId),
    forceIdPresence: false,
    options: {
      retry: 10,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchInterval: 60000
    }
  }) as { record: UserProgress; refetch: () => void }

  const wallet = userProgress?.wallet ?? EMPTY_OBJ
  const title = userProgress?.title ?? null
  // Guildas do participante. Com o bundle deixando de recortar por guilda, é
  // daqui que as telas decidem o que exibir — ver `shared/lib/guild/visibility`.
  const guildIds = userProgress?.guild_ids ?? EMPTY_ARR

  const finishedJourneys = userProgress?.finished_journeys ?? EMPTY_ARR
  const finishedStages = userProgress?.finished_stages ?? EMPTY_ARR
  const finishedLevels = userProgress?.finished_levels ?? EMPTY_ARR
  const finishedChallenges = userProgress?.finished_challenges ?? EMPTY_ARR
  const correctChallenges = userProgress?.correct_challenges ?? EMPTY_ARR
  const finishedMissions = userProgress?.finished_missions ?? EMPTY_ARR
  const finishedPartners = userProgress?.finished_partners ?? EMPTY_ARR
  const missionSubmissions = userProgress?.mission_submissions ?? EMPTY_ARR
  const participatingPrizes = userProgress?.participating_prizes ?? EMPTY_ARR
  const quizProgress = (userProgress?.quiz_progress as QuizProgress[]) ?? []

  const journeysSet = useMemo(
    () => new Set(finishedJourneys),
    [finishedJourneys]
  )
  const stagesSet = useMemo(() => new Set(finishedStages), [finishedStages])
  const levelsSet = useMemo(() => new Set(finishedLevels), [finishedLevels])
  const challengesSet = useMemo(
    () => new Set(finishedChallenges),
    [finishedChallenges]
  )
  const correctChallengesSet = useMemo(
    () => new Set(correctChallenges),
    [correctChallenges]
  )
  const missionsSet = useMemo(
    () => new Set(finishedMissions),
    [finishedMissions]
  )
  const partnersSet = useMemo(
    () => new Set(finishedPartners),
    [finishedPartners]
  )
  const quizProgressSet = useMemo(
    () =>
      new Set(quizProgress.map((progress: QuizProgress) => progress.quiz_id)),
    [quizProgress]
  )

  const journeyWasFinished = useCallback(
    (id: number) => journeysSet.has(id),
    [journeysSet]
  )
  const stageWasFinished = useCallback(
    (id: number) => stagesSet.has(id),
    [stagesSet]
  )
  const levelWasFinished = useCallback(
    (id: number) => levelsSet.has(id),
    [levelsSet]
  )
  const challengeWasFinished = useCallback(
    (id: number) => challengesSet.has(id),
    [challengesSet]
  )
  const challengeWasCorrect = useCallback(
    (id: number) => correctChallengesSet.has(id),
    [correctChallengesSet]
  )
  const missionWasFinished = useCallback(
    (id: number) => missionsSet.has(id),
    [missionsSet]
  )
  const partnerWasFinished = useCallback(
    (id: number) => partnersSet.has(id),
    [partnersSet]
  )
  const quizWasFinished = useCallback(
    (id: number) => quizProgressSet.has(id),
    [quizProgressSet]
  )

  return {
    userProgress,
    wallet,
    title,
    guildIds,
    finishedJourneys,
    finishedStages,
    finishedLevels,
    finishedChallenges,
    correctChallenges,
    finishedMissions,
    finishedPartners,
    missionSubmissions,
    quizProgress,
    participatingPrizes,

    journeyWasFinished,
    stageWasFinished,
    levelWasFinished,
    challengeWasFinished,
    challengeWasCorrect,
    missionWasFinished,
    partnerWasFinished,
    quizWasFinished,

    refetchProgress
  }
}
